import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Baseball,
  UsersThree,
  ChartBar,
  ChartLineUp,
  Wind,
  MapPin,
  ClockCounterClockwise,
  ListChecks,
  Globe,
  Timer,
  Target,
} from "@phosphor-icons/react";
import { GameDetailNav } from "../components/game-detail/GameDetailNav";
import { SectionCard } from "../components/game-detail/SectionCard";
import { CollapsibleSectionCard } from "../components/game-detail/CollapsibleSectionCard";
import { EmptyState } from "../components/shared/EmptyState";
import { TeamLogo } from "../components/shared/TeamLogo";
import { GameStatusBadge } from "../components/game-detail/GameStatusBadge";
import { FeatureCoverageBar } from "../components/game-detail/FeatureCoverageBar";
import { PitcherCard } from "../components/game-detail/PitcherCard";
import { OffenseCard } from "../components/game-detail/OffenseCard";
import { PredictionCard } from "../components/game-detail/PredictionCard";
import { FullGameOUCard } from "../components/game-detail/FullGameOUCard";
import { F5OUHeroCard } from "../components/game-detail/F5OUHeroCard";
import { NRFIYRFIHeroCard } from "../components/game-detail/NRFIYRFIHeroCard";
import { RunEnvironmentV2Card } from "../components/game-detail/RunEnvironmentV2Card";
import { F5RunEnvironmentCard } from "../components/game-detail/F5RunEnvironmentCard";
import { StatGrid } from "../components/game-detail/StatGrid";
import { SegmentTabs } from "../components/dashboard/SegmentTabs";
import { SEGMENTS } from "../constants/segments";
import { getGameIntelligence, getGamePrediction } from "../lib/api";

const GameDetail = () => {
  const { gameId } = useParams();
  const [segment, setSegment] = useState("FULL_GAME");
  const [detail, setDetail] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    getGameIntelligence(gameId, { segment })
      .then((data) => setDetail(data))
      .catch(() => setNotFound(true));
  }, [gameId, segment]);

  useEffect(() => {
    setPrediction(null);
    // No as_of passed here: the backend anchors the win-probability calc's as_of to a fixed
    // point-in-time cutoff (day before the game) regardless, and separately checks confirmed
    // lineups against the real current time -- see predictor.py::_live_confirmed_lineups.
    getGamePrediction(gameId)
      .then((data) => setPrediction(data))
      .catch(() => setPrediction(null));
  }, [gameId]);

  // Phase 6D: Over/Under probabilities are only computed when a threshold is supplied -- this
  // re-fetches the same (cached) prediction with total_threshold added, never mutating the
  // moneyline half of the response.
  const handleCalculateTotal = (thresholdValue) => {
    const params = {};
    const parsed = Number(thresholdValue);
    if (thresholdValue !== "" && !Number.isNaN(parsed)) {
      params.total_threshold = parsed;
    }
    getGamePrediction(gameId, params)
      .then((data) => setPrediction(data))
      .catch(() => {});
  };

  const game = detail?.game;
  const payload = detail?.payload;
  const audit = detail?.audit;
  const rest = payload?.rest;
  const recentForm = payload?.recent_form;
  const leagueContext = payload?.league_context;
  const park = payload?.park;
  const weather = payload?.weather;

  const restStats = (block) => [
    { label: "Rest Days", value: block?.rest_days },
    { label: "Games Last 3 Days", value: block?.games_last_3_days },
    { label: "Games Last 7 Days", value: block?.games_last_7_days },
    { label: "Doubleheader Today", value: block?.is_second_of_doubleheader ? "Yes" : block?.status === "available" ? "No" : null },
    { label: "Home/Road Sequence", value: null },
  ];

  return (
    <div data-testid="game-detail-page" className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6">
      <Link
        to="/"
        data-testid="game-detail-back-link"
        className="mb-4 flex items-center gap-1.5 font-mono text-xs text-zinc-500 hover:text-white"
      >
        <ArrowLeft size={14} /> Back to Dashboard
      </Link>

      {notFound && (
        <div
          data-testid="game-detail-not-found-banner"
          className="mb-4 rounded-md border border-dashed border-zinc-700 bg-eb-surface/60 px-4 py-2 font-mono text-xs text-zinc-500"
        >
          No live game data for this ID yet &mdash; showing structural preview.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
        <GameDetailNav />

        <div className="flex flex-col gap-5">
          <SectionCard id="overview" title="Teams" icon={Baseball} testId="section-overview">
            {game ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p data-testid="game-detail-matchup" className="flex items-center gap-2 font-heading text-xl text-white">
                      <TeamLogo team={game.away_team} size={28} testId="game-detail-logo-away" />
                      <span>{game.away_team?.name || game.away_team_id}</span>
                      <span className="text-sm text-zinc-500">@</span>
                      <TeamLogo team={game.home_team} size={28} testId="game-detail-logo-home" />
                      <span>{game.home_team?.name || game.home_team_id}</span>
                    </p>
                    <p className="mt-1 font-mono text-xs uppercase text-zinc-500">
                      {game.league_code || "League Unavailable"} &middot; {game.game_date || "Date Unavailable"}
                      {game.status === "final" ? ` \u00b7 ${game.away_score}-${game.home_score}` : ""}
                      {game.venue?.name ? ` \u00b7 ${game.venue.name}` : ""}
                    </p>
                  </div>
                  <GameStatusBadge status={game.status} testId="game-detail-status-badge" />
                </div>
                <SegmentTabs activeSegment={segment} onChange={setSegment} segments={SEGMENTS} />
                {Object.keys(game.segment_scores || {}).length > 0 && (
                  <table data-testid="segment-scoreboard" className="w-full max-w-sm border border-white/10 font-mono text-xs">
                    <thead>
                      <tr className="border-b border-white/10 text-zinc-500">
                        <th className="p-2 text-left">Segment</th>
                        <th className="p-2 text-right">Away</th>
                        <th className="p-2 text-right">Home</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["FIRST_INNING", "FIRST_FIVE", "FULL_GAME"]
                        .filter((seg) => game.segment_scores[seg])
                        .map((seg) => (
                          <tr key={seg} className="border-b border-white/5 text-zinc-300 last:border-0">
                            <td className="p-2 text-left">{seg.replace("_", " ")}</td>
                            <td className="p-2 text-right">{game.segment_scores[seg].away_runs}</td>
                            <td className="p-2 text-right">{game.segment_scores[seg].home_runs}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
                {detail?.as_of_date && (
                  <p data-testid="game-detail-as-of" className="font-mono text-[11px] text-zinc-600">
                    Feature data as of {detail.as_of_date} (day before this game &mdash; never uses same-day or later
                    information). Source: {detail.source}.
                  </p>
                )}
              </div>
            ) : (
              <EmptyState icon={Baseball} title="Matchup Unavailable" description="Team matchup will render here once game data is loaded." testId="empty-state-overview" />
            )}
          </SectionCard>

          <SectionCard id="full-game-ou" title="Full Game O/U" icon={ChartLineUp} testId="section-full-game-ou">
            <FullGameOUCard total={prediction?.total} onCalculateTotal={handleCalculateTotal} testId="full-game-ou-card" />
          </SectionCard>

          <SectionCard id="f5-ou" title="First 5 O/U" icon={Timer} testId="section-f5-ou">
            <F5OUHeroCard f5Total={prediction?.f5_total} testId="f5-ou-card" />
          </SectionCard>

          <SectionCard id="first-inning-ou" title="First Inning O/U (NRFI/YRFI)" icon={Target} testId="section-first-inning-ou">
            <NRFIYRFIHeroCard nrfiYrfi={prediction?.nrfi_yrfi} testId="nrfi-yrfi-card" />
          </SectionCard>

          <p data-testid="why-key-projection-divider" className="mt-1 font-mono text-[10px] uppercase tracking-wide text-zinc-600">
            Why / Key Projection Information
          </p>

          <SectionCard id="run-environment-v2" title="Run Environment V2 (Analytical)" icon={ChartBar} testId="section-run-environment-v2">
            <RunEnvironmentV2Card
              totalV2={prediction?.total_v2}
              awayLabel={game?.away_team?.name || "Away"}
              homeLabel={game?.home_team?.name || "Home"}
              testId="run-environment-v2-card"
            />
          </SectionCard>

          <CollapsibleSectionCard id="moneyline" title="Moneyline Prediction" icon={ChartLineUp} testId="section-moneyline">
            <PredictionCard
              prediction={prediction}
              awayLabel={game?.away_team?.name || "Away"}
              homeLabel={game?.home_team?.name || "Home"}
              testId="prediction-card"
            />
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="f5-details" title="First 5 Innings Details" icon={Timer} testId="section-f5-details">
            <F5RunEnvironmentCard
              f5Total={prediction?.f5_total}
              awayLabel={game?.away_team?.name || "Away"}
              homeLabel={game?.home_team?.name || "Home"}
              testId="f5-run-environment-card"
            />
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="pitchers" title="Starting Pitchers" icon={UsersThree} testId="section-pitchers">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <PitcherCard label={game?.away_team?.name || "Away"} pitcher={detail?.away_pitcher} block={payload?.pitcher?.away} testId="pitcher-card-away" />
              <PitcherCard label={game?.home_team?.name || "Home"} pitcher={detail?.home_pitcher} block={payload?.pitcher?.home} testId="pitcher-card-home" />
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="offense" title="Team Offense" icon={ChartBar} testId="section-offense">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <OffenseCard label={game?.away_team?.name || "Away"} block={payload?.offense?.away} testId="offense-card-away" />
              <OffenseCard label={game?.home_team?.name || "Home"} block={payload?.offense?.home} testId="offense-card-home" />
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="lineups" title="Lineups" icon={UsersThree} testId="section-lineups">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {["away", "home"].map((side) => {
                const block = payload?.lineup?.[side];
                return (
                  <div key={side} data-testid={`lineup-card-${side}`} className="rounded-lg border border-white/10 bg-eb-bg p-4">
                    <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{game?.[`${side}_team`]?.name || side}</span>
                    {block?.status === "available" ? (
                      <table data-testid={`lineup-table-${side}`} className="mt-2 w-full font-mono text-xs">
                        <tbody>
                          {block.entries.map((e) => (
                            <tr key={`${e.batting_order}-${e.player_id}`} className="border-b border-white/5 text-zinc-300 last:border-0">
                              <td className="py-1 pr-2 text-zinc-500">{e.batting_order}</td>
                              <td className="py-1">{e.full_name || "Unknown"}</td>
                              <td className="py-1 pl-2 text-right text-zinc-500">{e.position}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p data-testid={`lineup-unavailable-${side}`} className="mt-2 font-mono text-xs text-zinc-600">
                        Unavailable ({block?.reason || "Confirmed batting lineups are not ingested yet."})
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="bullpens" title="Bullpens" icon={UsersThree} testId="section-bullpens">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {["away", "home"].map((side) => {
                const block = payload?.bullpen?.[side];
                return (
                  <div key={side} data-testid={`bullpen-card-${side}`} className="rounded-lg border border-white/10 bg-eb-bg p-4">
                    <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{game?.[`${side}_team`]?.name || side}</span>
                    {block?.status === "available" ? (
                      <div className="mt-3">
                        <StatGrid
                          testIdPrefix={`bullpen-${side}`}
                          stats={[
                            { label: "Relief IP (last 3d)", value: block.relief_innings_pitched },
                            { label: "Relief Appearances", value: block.relief_appearances },
                            { label: "Relief Pitches", value: block.relief_pitches_thrown },
                            { label: "Workload", value: block.workload === "high_workload" ? "High" : "Normal" },
                          ]}
                        />
                      </div>
                    ) : (
                      <p data-testid={`bullpen-unavailable-${side}`} className="mt-2 font-mono text-xs text-zinc-400">
                        {block?.status === "insufficient_data" ? "Insufficient Data" : "Unavailable"} ({block?.reason || "Bullpen usage/workload data is not ingested yet."})
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="rest" title="Rest & Schedule" icon={ClockCounterClockwise} testId="section-rest">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div data-testid="rest-card-away" className="rounded-lg border border-white/10 bg-eb-bg p-4">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{game?.away_team?.name || "Away"}</span>
                <div className="mt-3">
                  <StatGrid testIdPrefix="rest-away" stats={restStats(rest?.away)} />
                </div>
              </div>
              <div data-testid="rest-card-home" className="rounded-lg border border-white/10 bg-eb-bg p-4">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{game?.home_team?.name || "Home"}</span>
                <div className="mt-3">
                  <StatGrid testIdPrefix="rest-home" stats={restStats(rest?.home)} />
                </div>
              </div>
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="ballpark-weather" title="Park & Weather" icon={MapPin} testId="section-ballpark-weather">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div data-testid="section-ballpark" className="flex flex-col gap-3">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Ballpark</span>
                <p data-testid="ballpark-name" className="font-heading text-lg text-white">{game?.venue?.name || "Unavailable"}</p>
                <StatGrid
                  testIdPrefix="ballpark"
                  stats={[
                    { label: "Location", value: game?.venue?.city },
                    { label: "Surface", value: game?.venue?.surface },
                    { label: "Roof Status", value: game?.venue?.roof_type },
                    { label: "Dimensions", value: game?.venue?.dimensions ? JSON.stringify(game.venue.dimensions) : null },
                  ]}
                />
                <p data-testid="ballpark-park-factor" className="font-mono text-xs text-zinc-500">
                  {park?.status === "available" ? (
                    <>
                      Run Park Factor: <span className="text-white">{park.run_factor}</span> (100 = neutral, based on{" "}
                      {park.sample?.years_used} season{park.sample?.years_used === 1 ? "" : "s"} /{" "}
                      {park.sample?.home_games} home games). HR Park Factor: Unavailable ({park.hr_factor_reason})
                    </>
                  ) : (
                    <>Run Park Factor: {park?.status === "insufficient_data" ? "Insufficient Data" : "Unavailable"} ({park?.reason || "requires multi-year run-differential analysis"})</>
                  )}
                </p>
              </div>
              <div data-testid="section-weather" className="flex flex-col gap-3">
                <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Weather</span>
                {weather?.status === "available" ? (
                  <StatGrid
                    testIdPrefix="weather"
                    stats={[
                      { label: "Condition", value: weather.condition },
                      { label: "Temperature", value: weather.temp_f ? `${weather.temp_f}\u00b0F` : null },
                      { label: "Wind", value: !weather.wind || weather.wind.includes(", None") ? "N/A (Dome)" : weather.wind },
                    ]}
                  />
                ) : (
                  <EmptyState icon={Wind} title="Weather Unavailable" description={weather?.reason || "No weather data source has been integrated yet."} testId="empty-state-weather" />
                )}
              </div>
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="league-context" title="League / Season Context" icon={Globe} testId="section-league-context">
            <div className="flex flex-col gap-3">
              <p data-testid="league-context-header" className="font-heading text-lg text-white">
                {game?.league_code || "League"} &mdash; {leagueContext?.year || "Season Unavailable"}
              </p>
              <StatGrid
                testIdPrefix="league-context"
                stats={[
                  { label: "Avg Runs/Team/Game", value: leagueContext?.avg_runs_per_team_per_game },
                  { label: "Games Counted", value: leagueContext?.games_counted },
                  { label: "HR Environment", value: null },
                  { label: "Strikeout Environment", value: null },
                  { label: "Walk Environment", value: null },
                ]}
              />
              <p className="font-mono text-[10px] text-zinc-600">
                MLB, NPB and LMB are tracked as separate statistical environments &mdash; context above never mixes leagues or seasons.
              </p>
            </div>
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="recent-performance" title="Recent Performance" icon={ClockCounterClockwise} testId="section-recent-performance">
            {recentForm?.home?.status === "available" || recentForm?.away?.status === "available" ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {["away", "home"].map((side) => (
                  <div key={side} data-testid={`recent-form-${side}`} className="rounded-lg border border-white/10 bg-eb-bg p-4">
                    <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{game?.[`${side}_team`]?.name || side}</span>
                    {recentForm?.[side]?.status === "available" ? (
                      <table className="mt-2 w-full font-mono text-xs">
                        <tbody>
                          {recentForm[side].results.slice(0, 5).map((r) => (
                            <tr key={r.game_id} className="border-b border-white/5 text-zinc-300 last:border-0">
                              <td className="py-1 text-zinc-500">{r.game_date}</td>
                              <td className="py-1 text-right">{r.runs_for}-{r.runs_against}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="mt-2 font-mono text-xs text-zinc-600">Unavailable</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={ClockCounterClockwise} title="Recent Form Unavailable" description="No completed games with this segment recorded yet." testId="empty-state-recent-performance" />
            )}
          </CollapsibleSectionCard>

          <CollapsibleSectionCard id="coverage" title="Data Coverage" icon={ListChecks} testId="section-coverage">
            <FeatureCoverageBar coverage={detail?.feature_coverage} availability={audit?.availability} payload={payload} />
          </CollapsibleSectionCard>
        </div>
      </div>
    </div>
  );
};

export default GameDetail;
